import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  TextField,
  IconButton,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  X,
  Trash2,
  Plus,
  Save,
  Calendar,
  DollarSign,
  Building,
  CreditCard,
  CheckCircle2,
  Fingerprint,
  Package,
  Hash
} from 'lucide-react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import api from '../services/api';
import { format } from 'date-fns';

const SaleDetailsModal = ({ open, onClose, saleId }) => {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [selectedService, setSelectedService] = useState('');
  const [quantity, setQuantity] = useState(1);

  const [loading, setLoading] = useState(false);

  const [saleDate, setSaleDate] = useState(new Date());
  const [tip, setTip] = useState(0);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [status, setStatus] = useState('COMPLETED');
  const [localSaleId, setLocalSaleId] = useState(null);

  const depsLoadedRef = useRef(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isNewSale = !saleId;

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchDependenciesOnce = async () => {
    if (depsLoadedRef.current) return;
    try {
      const [servicesRes, hotelsRes, paymentMethodsRes] = await Promise.all([
        api.get('/services'),
        api.get('/hotels'),
        api.get('/payment_methods'),
      ]);
      setServices(servicesRes.data || []);
      setHotels(hotelsRes.data || []);
      setPaymentMethods(paymentMethodsRes.data || []);
      depsLoadedRef.current = true;
    } catch (err) {
      console.error('❌ Error loading dependencies:', err);
      showSnackbar('Error loading essential data', 'error');
      // não marca como loaded, pra tentar de novo numa próxima abertura
      depsLoadedRef.current = false;
    }
  };

  const resetForNewSale = () => {
    setItems([]);
    setSaleDate(new Date());
    setTip(0);
    setSelectedHotel('');
    setSelectedPaymentMethod('');
    setStatus('COMPLETED');
    setLocalSaleId(null);
    setSelectedService('');
    setQuantity(1);
  };

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        await fetchDependenciesOnce();
        if (cancelled) return;

        if (isNewSale) {
          resetForNewSale();
          return;
        }

        // ✅ 1 chamada só: sale + items
        const { data } = await api.get(`/sales/${saleId}/full`);
        if (cancelled) return;

        // backend manda sale_time como 'YYYY-MM-DD'
        const parsedDate = data?.sale_time ? new Date(`${data.sale_time}T00:00:00`) : new Date();
        setSaleDate(parsedDate);

        setTip(Number(data?.tip || 0));
        setSelectedHotel(data?.hotel_id ? String(data.hotel_id) : '');
        setStatus(data?.status || 'PENDING');
        setLocalSaleId(data?.local_sale_id || null);

        // items já vem pronto
        const fullItems = Array.isArray(data?.items) ? data.items : [];
        // garantir shape igual ao resto do código
        setItems(fullItems.map((it) => ({
          service_id: Number(it.service_id),
          name: it.name,
          price: Number(it.price),
          quantity: Number(it.quantity),
        })));

        // payment_method no banco é string, precisamos achar o id
        if (data?.payment_method) {
          const pm = paymentMethods.find(
            (p) => p.method?.toLowerCase().trim() === data.payment_method.toLowerCase().trim()
          );
          setSelectedPaymentMethod(pm ? String(pm.id) : '');
        } else {
          setSelectedPaymentMethod('');
        }
      } catch (err) {
        console.error('❌ Error loading sale data:', err);
        showSnackbar('Error loading sale data', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, saleId]);

  const handleAddItem = () => {
    if (!selectedService || quantity <= 0) {
      showSnackbar('Please select a service and a valid quantity', 'warning');
      return;
    }
    const service = services.find((s) => s.id === Number(selectedService));
    if (!service) return;

    setItems((prev) => [
      ...prev,
      { service_id: service.id, name: service.name, price: Number(service.price), quantity: Number(quantity) }
    ]);

    setSelectedService('');
    setQuantity(1);
  };

  const handleDeleteItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

  const handleDeleteSale = async () => {
    if (isNewSale || !window.confirm('Are you sure you want to delete this sale?')) return;
    setLoading(true);
    try {
      await api.delete(`/sales/${saleId}`);
      showSnackbar('Sale deleted successfully');
      setTimeout(() => onClose(true), 600);
    } catch (err) {
      showSnackbar('Error deleting sale', 'error');
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (items.length === 0 || !selectedHotel || !selectedPaymentMethod) {
      showSnackbar('Please fill all required fields and add items.', 'warning');
      return;
    }

    setLoading(true);

    const selectedMethod = paymentMethods.find(pm => String(pm.id) === String(selectedPaymentMethod));

    const saleData = {
      items: items.map(item => ({ service_id: Number(item.service_id), quantity: Number(item.quantity) })),
      sale_time: format(saleDate, 'yyyy-MM-dd'),
      hotel_id: Number(selectedHotel),
      payment_method: selectedMethod ? selectedMethod.method : '',
      tip: Number(tip),
      status: status,
    };

    const apiCall = isNewSale
      ? api.post('/sales', saleData)
      : api.put(`/sales/${saleId}`, saleData);

    apiCall
      .then(() => {
        showSnackbar(isNewSale ? 'Sale created successfully' : 'Sale updated successfully');
        setTimeout(() => onClose(true), 600);
      })
      .catch(err => {
        console.error('Error saving sale:', err);
        showSnackbar(`Error saving: ${err.response?.data?.msg || err.message}`, 'error');
      })
      .finally(() => setLoading(false));
  };

  const calculateSubtotal = () => items.reduce((total, item) => total + (Number(item.price) * Number(item.quantity)), 0);

  const getStatusColor = (statusValue) => (statusValue === 'COMPLETED' ? '#4caf50' : '#ff9800');

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose()}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Package size={24} color="#1976d2" />
            <span>{isNewSale ? 'New Sale' : `Sale #${saleId}`}</span>
          </Box>
          {loading && <CircularProgress size={24} />}
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#333' }}>
            Sale Information
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Sale Date"
                value={saleDate}
                onChange={setSaleDate}
                disabled={loading}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Calendar size={18} color="#666" />
                        </InputAdornment>
                      ),
                    },
                  }
                }}
              />
            </LocalizationProvider>

            <TextField
              label="Tip"
              type="number"
              value={tip}
              onChange={(e) => setTip(parseFloat(e.target.value) || 0)}
              fullWidth
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DollarSign size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              label="Hotel"
              value={selectedHotel}
              onChange={(e) => setSelectedHotel(e.target.value)}
              fullWidth
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Building size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
            >
              {hotels.map((hotel) => (
                <MenuItem key={hotel.id} value={String(hotel.id)}>
                  {hotel.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Payment Method"
              value={selectedPaymentMethod}
              onChange={(e) => setSelectedPaymentMethod(e.target.value)}
              fullWidth
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditCard size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
            >
              {paymentMethods.map((method) => (
                <MenuItem key={method.id} value={String(method.id)}>
                  {method.method}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              fullWidth
              
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CheckCircle2 size={18} style={{ color: getStatusColor(status) }} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
            </TextField>

            {localSaleId && (
              <TextField
                label="Local ID"
                value={localSaleId}
                fullWidth
                disabled={true}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Fingerprint size={18} color="#666" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#333' }}>
            Add Items
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <TextField
              select
              label="Service"
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              fullWidth
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Package size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
            >
              {services.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name} - ${parseFloat(s.price).toFixed(2)}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(parseFloat(e.target.value))}
                inputProps={{ min: 0.1, step: 0.1 }}
                disabled={loading}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Hash size={18} color="#666" />
                    </InputAdornment>
                  ),
                }}
              />

              <IconButton
                onClick={handleAddItem}
                disabled={loading || !selectedService}
                sx={{
                  background: 'linear-gradient(to right, #4caf50, #388e3c)',
                  color: 'white',
                  '&:hover': { background: 'linear-gradient(to right, #45a049, #2e7d32)' },
                  '&:disabled': { background: '#e0e0e0', color: '#999' },
                  minWidth: 48,
                  height: 48,
                }}
              >
                <Plus size={20} />
              </IconButton>
            </Box>
          </Box>

          <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#333' }}>
            Sale Items
          </Typography>

          {items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px dashed #ddd' }}>
              <Typography color="text.secondary">No items added</Typography>
            </Box>
          ) : (
            <>
              <List sx={{ bgcolor: '#f9f9f9', borderRadius: 2, mb: 2 }}>
                {items.map((item, idx) => (
                  <ListItem
                    key={idx}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteItem(idx)}
                        disabled={loading}
                        sx={{ color: '#f44336', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' } }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    }
                    sx={{ bgcolor: 'white', mb: 1, borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                  >
                    <ListItemText
                      primary={item.name}
                      secondary={`Qty: ${item.quantity} × $${parseFloat(item.price).toFixed(2)} = $${(item.quantity * item.price).toFixed(2)}`}
                    />
                  </ListItem>
                ))}
              </List>

              <Box sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Subtotal: ${calculateSubtotal().toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Tip: ${parseFloat(tip).toFixed(2)}
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#333', mt: 1 }}>
                  Total: ${(calculateSubtotal() + parseFloat(tip)).toFixed(2)}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            onClick={() => onClose()}
            disabled={loading}
            startIcon={<X size={18} />}
            fullWidth
            sx={{
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              '&:hover': { backgroundColor: '#e0e0e0' },
            }}
          >
            Cancel
          </Button>

          {!isNewSale && (
            <Button
              onClick={handleDeleteSale}
              disabled={loading}
              startIcon={<Trash2 size={18} />}
              fullWidth
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(to right, #f44336, #d32f2f)',
                color: '#fff',
                '&:hover': { background: 'linear-gradient(to right, #e53935, #c62828)' },
              }}
            >
              Delete Sale
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={<Save size={18} />}
            fullWidth
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(to right, #1976d2, #0d47a1)',
              color: '#fff',
              '&:hover': { background: 'linear-gradient(to right, #1565c0, #003c8f)' },
            }}
          >
            {isNewSale ? 'Create Sale' : 'Save Changes'}
          </Button>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaleDetailsModal;
